import React, { useEffect, useState } from "react";
import { IconKeyboardArrowRightBlue, LogoCurrentBlue } from "assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import {
  fetchTransactionNotification,
  transactionCleanData,
} from "reducers/transactionSlice";
import { Link } from "react-router-dom";
import {
  transactionNotificationText,
  questNotificationText,
} from "common/helper/text.helper";
import { useGetQuestNotificationsQuery } from "reducers/questApi";
import { getDiffTime, UTCToJapanTime } from "common/helper/time.helper";
import { useCommonContext } from "contexts/CommonContext";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS } from "config/apiConfig";
import { ConversationInquiryStatusEnum } from "common/enums/ConversationInquiryStatus.enum";
import CompanyProfileDashboard from "components/topScreen/CompanyProfileDashboard";

const Dashboard = () => {
  const { setHeaderTitle } = useCommonContext();
  const { userData } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const { transactionNotificationData } = useSelector(
    (state: RootState) => state.transaction,
  );
  const { data: questNotificationData } = useGetQuestNotificationsQuery();
  const questNotifications = questNotificationData?.data ?? [];
  const [conversionInquiry, setConversationInquiry] = useState<any>([]);
  useEffect(() => {
    fetchConversationInquiry();
    dispatch(fetchTransactionNotification());
    setHeaderTitle("ダッシュボード");
    return () => {
      dispatch(transactionCleanData());
    };
  }, []);

  const fetchConversationInquiry = () => {
    axiosInstance
      .get(API_ENDPOINTS.CONVERSATION_INQUIRY, {
        params: {
          limit: 5,
          sort: "id:desc",
          status: ConversationInquiryStatusEnum.PROCESSING,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          setConversationInquiry(response.data.data);
        }
      });
  };
  const formatTime = (time: string) => {
    const timeJapan = UTCToJapanTime(time);
    return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
  };
  return (
    <div className="self-stretch min-h-[752px] md:min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[20px] md:gap-10 overflow-hidden">
      <div className="md:hidden flex w-full h-[67px] justify-center items-center aspect-[180/67]">
        <LogoCurrentBlue className="w-[180px] h-[73px] shrink-0" />
      </div>
      <div className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
        ようこそ、{userData.company_name}さま
      </div>
      <CompanyProfileDashboard companyCode={userData.code} role="buyer" />
      <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
        {/*Quest achievement notice*/}
        {questNotifications.length > 0 && (
          <div className="self-stretch flex flex-col justify-start items-start">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    クエスト達成のお知らせ
                  </div>
                </div>
              </div>
            </div>
            {/*Contents*/}
            <div className="flex flex-col justify-center items-center gap-[5px] self-stretch">
              <div className="flex flex-col items-start self-stretch">
                {questNotifications.map((notice) => (
                  <div
                    key={notice.company_quest_id}
                    className="md:h-[42px] flex py-[10px] justify-center items-center gap-[10px] self-stretch border-b border-neutral-border bg-white"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-[7px] md:gap-[10px] flex-[1_0_0]">
                      <div className="md:max-h-[22px] md:h-[22px] flex-[1_0_0] text-black font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                        {questNotificationText(notice)}
                      </div>
                      <div className="h-[18px] md:h-[19px] text-blue-900 text-left font-['Noto Sans JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
                        {getDiffTime(notice.claimed_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="self-stretch flex flex-col justify-start items-start">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                  取引についてのお知らせ
                </div>
              </div>
            </div>
          </div>
          {/*Contents*/}
          <div className="flex flex-col justify-center items-center gap-[5px] self-stretch">
            <div className="flex flex-col items-start self-stretch">
              {transactionNotificationData?.data.length === 0 ? (
                <div className="h-[80px] md:h-[110px] flex py-[10px] md:py-[15px] items-center justify-center self-stretch border-b border-neutral-border bg-white">
                  <div className="text-gray-500 font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                    表示する内容がありません
                  </div>
                </div>
              ) : (
                ""
              )}
              {/*Items*/}
              {transactionNotificationData?.data.map((transaction, index) => {
                return (
                  <Link
                    to={`/buyer/product/${transaction.product.code}/transaction/${transaction.buyer_id}/${transaction.seller_id}`}
                    key={index}
                    className="h-[89px] md:h-[42px] flex py-[10px] justify-center items-center gap-[10px] self-stretch border-b border-neutral-border bg-white"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-[7px] md:gap-[10px] flex-[1_0_0]">
                      <div className="max-h-[44px] md:max-h-[22px] md:h-[22px] flex-[1_0_0] text-black font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                        {transactionNotificationText(transaction)}
                      </div>
                      <div className="h-[18px] md:h-[19px] text-primary text-left font-['Noto Sans JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
                        {getDiffTime(transaction.commonDate)}
                      </div>
                    </div>
                    <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                  お知らせ
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-center items-center gap-[5px]">
            <div className="self-stretch flex flex-col justify-start items-start">
              <div className="flex flex-col items-start self-stretch">
                {conversionInquiry.length === 0 ? (
                  <div className="h-[80px] md:h-[110px] flex py-[10px] md:py-[15px] items-center justify-center self-stretch border-b border-neutral-border bg-white">
                    <div className="text-gray-500 font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                      表示する内容がありません
                    </div>
                  </div>
                ) : (
                  ""
                )}

                {conversionInquiry.map((item: any) => (
                  <Link
                    to="/buyer/conversation-inquiry"
                    key={item.id}
                    className="self-stretch flex flex-col justify-start items-start"
                  >
                    <div
                      data-プロパティ1="base"
                      className="self-stretch py-2.5 bg-white border-b border-neutral-300 inline-flex justify-center items-center gap-2.5"
                    >
                      <div className="flex-1 flex flex-col md:flex-row justify-start items-start md:items-center gap-[5px] md:gap-16">
                        <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                          管理者があなたのトピック「{item.subject}
                          」に返信しました
                        </div>
                        <div className="text-right justify-start text-blue-900 text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
                          {formatTime(item.created_at)}
                        </div>
                      </div>
                      <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
